import { apiClient } from '@/core/apiClient';

export type EmailCampaign = {
  id: number;
  name: string;
  description?: string | null;
  subject: string;
  preheader?: string | null;
  htmlContent: string;
  textContent?: string | null;
  fromEmail: string;
  replyTo?: string | null;
  communicationType: string;
  status:
    | 'DRAFT'
    | 'PREPARING'
    | 'READY'
    | 'SCHEDULED'
    | 'RUNNING'
    | 'PAUSED'
    | 'COMPLETED'
    | 'COMPLETED_WITH_ERRORS'
    | 'CANCELLED'
    | 'FAILED'
    | 'WAITING_FOR_QUOTA';
  recipientFilter: RecipientFilter;
  totalRecipients: number;
  pendingCount: number;
  acceptedCount: number;
  deliveredCount: number;
  failedCount: number;
  bounceCount: number;
  complaintCount: number;
  skippedCount: number;
  maxRetries: number;
  sesConfigurationSet?: string | null;
  lastError?: string | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
};

export type RecipientFilter = {
  sources?: string[];
  importId?: number;
  createdFrom?: string;
  createdTo?: string;
};

export type RecipientPreview = {
  eligible: number;
  excluded: { invalid: number; suppressed: number; unsubscribed: number };
  bySource: Record<string, number>;
};

export type AuditLogEntry = {
  id: number;
  action: string;
  actorUserId?: number | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
};

export type CampaignDashboard = {
  campaign: EmailCampaign;
  bySource: Record<string, { total: number; delivered: number; bounced: number }>;
};

export type CreateCampaignPayload = {
  name: string;
  description?: string;
  subject: string;
  preheader?: string;
  htmlContent: string;
  textContent?: string;
  fromEmail: string;
  replyTo?: string;
  communicationType?: string;
};

export const emailCampaignsApi = {
  create: async (payload: CreateCampaignPayload): Promise<EmailCampaign> => {
    const res = await apiClient.post('/admin/email-campaigns', payload);
    return res.data;
  },

  list: async (status?: string, page = 1, pageSize = 20): Promise<{ items: EmailCampaign[]; total: number }> => {
    const res = await apiClient.get('/admin/email-campaigns', { params: { status, page, pageSize } });
    return res.data;
  },

  findById: async (id: number): Promise<EmailCampaign> => {
    const res = await apiClient.get(`/admin/email-campaigns/${id}`);
    return res.data;
  },

  update: async (id: number, payload: Partial<CreateCampaignPayload>): Promise<EmailCampaign> => {
    const res = await apiClient.patch(`/admin/email-campaigns/${id}`, payload);
    return res.data;
  },

  previewRecipients: async (id: number, filter: RecipientFilter): Promise<RecipientPreview> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/preview-recipients`, filter);
    return res.data;
  },

  generateSnapshot: async (id: number, filter: RecipientFilter): Promise<{ total: number }> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/generate-snapshot`, filter);
    return res.data;
  },

  testSend: async (id: number, to: string): Promise<{ messageId: string }> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/test-send`, { to });
    return res.data;
  },

  start: async (id: number): Promise<EmailCampaign> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/start`);
    return res.data;
  },

  pause: async (id: number): Promise<EmailCampaign> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/pause`);
    return res.data;
  },

  resume: async (id: number): Promise<EmailCampaign> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/resume`);
    return res.data;
  },

  cancel: async (id: number): Promise<EmailCampaign> => {
    const res = await apiClient.post(`/admin/email-campaigns/${id}/cancel`);
    return res.data;
  },

  dashboard: async (id: number): Promise<CampaignDashboard> => {
    const res = await apiClient.get(`/admin/email-campaigns/${id}/dashboard`);
    return res.data;
  },

  auditLog: async (id: number): Promise<AuditLogEntry[]> => {
    const res = await apiClient.get(`/admin/email-campaigns/${id}/audit-log`);
    return res.data;
  },
};
